/**
 * Colour-mode preference — light, dark, or follow the operating system. A copy
 * of skySchedule's client/src/lib/theme.ts, so both apps behave the same.
 *
 * SkyProvider holds the mode, resolves "system" against prefers-color-scheme
 * (following a live OS change), and toggles `sky-dark` / `sky-light` on <html>;
 * the design-system components and the app's `dark:` utilities (index.css's
 * @custom-variant) key off `sky-dark`. ThemeSync (components/theme) mirrors
 * the resolved mode into `color-scheme` and remembers the choice, and the
 * inline script in index.html applies it before first paint so a dark-mode
 * user never sees a white flash while the bundle loads.
 *
 * The preference is a per-device convenience, not account state: it lives in
 * localStorage, every read and write is guarded (private windows and blocked
 * storage throw), and a missing or unreadable value falls back to "system", so
 * the app follows the operating system until the user picks a mode.
 *
 * Deliberately NOT the legacy `sky-user-theme` key: authService copies the
 * user_preferences.theme column (which defaults to 'Light' for every row) into
 * it at login, so reading it would pin every existing user to Light.
 *
 * KEEP IN SYNC with the inline script in index.html: same storage key, same
 * accepted values, same "system" fallback, same public-page exclusions.
 */
import type { ThemeMode } from "@skypointcloud/skypoint-ui";

export type ThemePreference = ThemeMode;

export const THEME_STORAGE_KEY = "skyhire:theme";
export const DEFAULT_THEME_PREFERENCE: ThemePreference = "system";

const PREFERENCES: readonly ThemePreference[] = ["light", "dark", "system"];

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === "string" && (PREFERENCES as readonly string[]).includes(value);
}

export function readThemePreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : DEFAULT_THEME_PREFERENCE;
  } catch {
    return DEFAULT_THEME_PREFERENCE;
  }
}

export function writeThemePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Storage unavailable: the choice still applies for this page view.
  }
}

/** Mirror the resolved mode onto <html> for native controls. */
export function applyColorScheme(
  isDark: boolean,
  root: HTMLElement = document.documentElement,
): void {
  // Native date/time inputs, scrollbars and form controls follow color-scheme.
  root.style.colorScheme = isDark ? "dark" : "light";
}

/**
 * Whether this page follows the user's appearance preference. Public pages
 * (tokenized links sent to candidates) and the candidate portal stay light.
 * Mirrors the URL tests index.tsx uses to route them, and the copy of this
 * check in index.html's pre-paint script.
 */
export function isThemedPath(href: string, pathname: string, isCandidateFlow: boolean): boolean {
  if (
    /\/(survey|sign|reupload|share)\/|\/interview\/cancel|\/easy-book|\/documents\/upload/.test(
      href,
    )
  )
    return false;
  if (pathname === "/candidate" || pathname.startsWith("/candidate/")) return false;
  if (pathname === "/callback" && isCandidateFlow) return false;
  return true;
}
